// Figma: https://www.figma.com/design/nykbXADUTliE6d7xv6FjcQ/NEW-TSAB-Shared-Customer-Library?node-id=4185-31323
// Price breakdown modal — shown when tapping the ⓘ on a fixed-price SelectedPayment row.
// Works with both real booking prices (priceComponents + vat) and pre-booking estimates
// (finalPrice / fullPrice / discountComponents).

import { useTranslation } from 'react-i18next'

const priceOrder = ['basePrice', 'fixedAdditionalFee', 'waitingFee', 'tip']

const InfoFilledIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0" aria-hidden="true">
    <g clipPath="url(#pim-clip)">
      <path fillRule="evenodd" clipRule="evenodd" d="M8 14.4333C11.553 14.4333 14.4333 11.553 14.4333 8C14.4333 4.44697 11.553 1.56667 8 1.56667C4.44697 1.56667 1.56667 4.44697 1.56667 8C1.56667 11.553 4.44697 14.4333 8 14.4333ZM8 15.5C12.1421 15.5 15.5 12.1421 15.5 8C15.5 3.85786 12.1421 0.5 8 0.5C3.85786 0.5 0.5 3.85786 0.5 8C0.5 12.1421 3.85786 15.5 8 15.5Z" fill="#17181A"/>
      <rect x="7.2" y="4" width="1.6" height="1.6" rx="0.8" fill="#17181A"/>
      <rect x="7.467" y="6.667" width="1.067" height="5.333" rx="0.213" fill="#17181A"/>
    </g>
    <defs>
      <clipPath id="pim-clip"><rect width="16" height="16" fill="white"/></clipPath>
    </defs>
  </svg>
)

export default function PriceInfoModal({ price, onClose }) {
  const { t } = useTranslation()

  // Real booking price: has priceComponents array
  const priceComponents = price?.priceComponents
    ? price.priceComponents
        .filter(p => p.type !== 'fixedPrice')
        .sort((a, b) => {
          const ia = priceOrder.indexOf(a.type)
          const ib = priceOrder.indexOf(b.type)
          if (ia === -1 && ib === -1) return 0
          if (ia === -1) return 1
          if (ib === -1) return -1
          return ia - ib
        })
    : null

  const hasDiscount = price?.discount && price?.discountComponents?.length > 0
  const hasVat = !!price?.vat
  const tip = Number(price?.tippedAmount) || 0
  const hasTip = tip > 0

  // Build rows array — used for aligned two-column layout
  const rows = []

  if (priceComponents) {
    priceComponents.forEach((p, i) => {
      // The API sends a localized display name per component (e.g. "Bilbarnstol" for a
      // vehicleOption); the apps show that. The type translation is only a fallback, since
      // several add-ons would otherwise all read as "Tillval".
      rows.push({ key: `pc-${i}`, label: p.name || t('price_' + p.type), amount: `${Math.round(p.amount)} kr` })
    })
  } else if (price?.fullPrice && price.fullPrice !== price.finalPrice) {
    // Estimate price: show full (undiscounted) price as base row
    rows.push({ key: 'base', label: t('price_basePrice'), amount: `${price.fullPrice} kr` })
  }

  if (hasDiscount) {
    price.discountComponents.forEach((d, i) => {
      const label = d.name + (d.type === 'voucher' && d.code ? ` ${d.code}` : '')
      rows.push({ key: `d-${i}`, label, amount: `-${Math.round(d.amount)} kr` })
    })
  }

  return (
    <div>
      {/* Header */}
      <div className="flex gap-1 items-center px-4 pt-4">
        <InfoFilledIcon />
        <p className="text-headline font-semibold uppercase text-on-surface">
          {t('label-price-information')}
        </p>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4 px-4 pt-3 pb-4 text-on-surface-variant text-body-sm">

        {/* Price component rows */}
        {rows.length > 0 && (
          <div className="flex gap-1.5 items-start">
            <div className="flex-1 flex flex-col gap-1.5 min-w-0">
              {rows.map(r => (
                <span key={r.key} className="font-medium">{r.label}</span>
              ))}
            </div>
            <div className="flex flex-col gap-1.5 items-end shrink-0 text-right whitespace-nowrap">
              {rows.map(r => (
                <span key={r.key}>{r.amount}</span>
              ))}
            </div>
          </div>
        )}

        {/* Total + VAT */}
        <div className="flex gap-1.5 items-start">
          <div className="flex-1 flex flex-col gap-0.5 min-w-0">
            <p className="text-headline font-semibold uppercase">{t('label-total')}</p>
            {hasVat && <p>{t('price_vat')}</p>}
          </div>
          <div className="flex flex-col gap-2 items-end shrink-0 text-right whitespace-nowrap">
            <span className="font-medium">{price?.finalPrice} kr</span>
            {hasVat && <span>{price.vat.toString().replace('.', ',')} kr</span>}
          </div>
        </div>

        {/* Tip + total incl. tip — tip is paid on top of the price, so keep it visually separated */}
        {hasTip && (
          <div className="flex flex-col gap-1.5 pt-4" style={{ borderTop: '1px solid rgba(0, 0, 0, 0.10)' }}>
            <div className="flex gap-1.5">
              <span className="flex-1 font-medium">{t('price_tip')}</span>
              <span className="shrink-0 whitespace-nowrap">{Math.round(tip)} kr</span>
            </div>
            <div className="flex gap-1.5 items-baseline">
              <span className="flex-1">
                <span className="text-headline font-semibold uppercase">{t('label-total')}</span> {t('label-incl-tip')}
              </span>
              <span className="shrink-0 whitespace-nowrap font-medium">{Math.round((price?.finalPrice || 0) + tip)} kr</span>
            </div>
          </div>
        )}

      </div>

      {/* OK button */}
      <button
        type="button"
        onClick={onClose}
        className="w-full py-3 bg-on-surface text-on-primary text-btn font-semibold uppercase tracking-[0.68px] leading-5"
      >
        OK
      </button>
    </div>
  )
}
